import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Camera } from "lucide-react";
import type { ParsedBarcode } from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { ApiClientError, getLocalizedError } from "@/lib/api";

/*
 * Ô quét dùng chung cho màn kho (18/09/2026).
 *
 * Máy quét cầm tay chuẩn là loại "bàn phím": quét xong nó gõ nội dung mã vạch rồi nhấn Enter vào ô đang chọn — nên ô
 * này luôn tự lấy tiêu điểm và xử lý Enter. Điện thoại không có máy quét thì dùng camera: trình duyệt nào có
 * BarcodeDetector (Chrome trên Android) mới hiện nút camera; trình duyệt khác vẫn gõ tay hoặc nối máy quét Bluetooth.
 */

interface DetectedBarcode { rawValue: string }
interface BarcodeDetectorLike { detect(source: HTMLVideoElement): Promise<DetectedBarcode[]> }
interface BarcodeDetectorCtor {
  new (options: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}
const detectorCtor = (): BarcodeDetectorCtor | null =>
  typeof window !== "undefined" && "BarcodeDetector" in window && !!navigator.mediaDevices?.getUserMedia
    ? (window as unknown as { BarcodeDetector: BarcodeDetectorCtor }).BarcodeDetector
    : null;

export function ScanField({ id, label, placeholder, onScan, busy, autoFocus = true }: {
  id: string;
  label: string;
  placeholder?: string;
  onScan: (value: string) => void;
  busy?: boolean;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [camera, setCamera] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const cameraAvailable = detectorCtor() !== null;
  useEffect(() => {
    if (autoFocus && !busy) ref.current?.focus();
  }, [autoFocus, busy]);
  const submit = (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    setValue("");
    onScan(v);
  };
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="flex gap-2">
        <Input
          id={id}
          ref={ref}
          className="h-12 flex-1 font-mono text-base"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="search"
          placeholder={placeholder}
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit(value);
            }
          }}
        />
        <Button type="button" variant="secondary" className="h-12" disabled={busy || !value.trim()} onClick={() => submit(value)}>
          {t("scan.go")}
        </Button>
        {cameraAvailable && (
          <Button type="button" variant="secondary" className="h-12" disabled={busy} aria-label={t("scan.camera")} onClick={() => setCamera(true)}>
            <Camera className="size-5" aria-hidden="true" />
          </Button>
        )}
      </div>
      {camera && (
        <CameraScanner
          onResult={(raw) => {
            setCamera(false);
            submit(raw);
          }}
          onClose={() => setCamera(false)}
        />
      )}
    </div>
  );
}

/** Quét bằng camera sau: mở luồng video, dò mã 4 lần/giây, đọc được mã đầu tiên thì trả về và tắt camera. */
function CameraScanner({ onResult, onClose }: { onResult: (raw: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState(false);
  const done = useRef(onResult);
  useEffect(() => {
    done.current = onResult;
  });
  useEffect(() => {
    let stream: MediaStream | null = null;
    let stopped = false;
    let timer: number | undefined;
    const Ctor = detectorCtor();
    void (async () => {
      try {
        if (!Ctor) throw new Error("no_detector");
        const wanted = ["code_128", "qr_code", "data_matrix", "ean_13", "code_39"];
        const supported = Ctor.getSupportedFormats ? await Ctor.getSupportedFormats() : wanted;
        const detector = new Ctor({ formats: wanted.filter((f) => supported.includes(f)) });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        // Đóng hộp quét trong lúc trình duyệt còn hỏi quyền: lúc dọn dẹp chạy, luồng chưa có nên chưa tắt được — tắt ở đây,
        // không thì đèn camera sáng mãi tới khi đóng tab.
        if (stopped || !video.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        video.current.srcObject = stream;
        await video.current.play();
        const tick = async () => {
          if (stopped || !video.current) return;
          try {
            const found = await detector.detect(video.current);
            const raw = found.find((f) => f.rawValue?.trim())?.rawValue;
            if (raw) {
              stopped = true;
              done.current(raw);
              return;
            }
          } catch {
            /* khung hình chưa sẵn sàng — thử lại lượt sau */
          }
          timer = window.setTimeout(() => void tick(), 250);
        };
        void tick();
      } catch {
        if (!stopped) setError(true);
      }
    })();
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);
  return (
    <Modal title={t("scan.cameraTitle")} closeLabel={t("common.close")} onClose={onClose}>
      <div className="space-y-3 p-4">
        {error ? (
          <p role="alert" className="text-sm text-danger">{t("scan.cameraError")}</p>
        ) : (
          <>
            <video ref={video} playsInline muted className="aspect-video w-full rounded-sm bg-ink object-cover" />
            <p className="text-sm text-ink-soft">{t("scan.cameraHint")}</p>
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * Nội dung một lần quét tem lô: số lô, và mã hàng khi tem mang cả mã (HISENSE:mã:lô). Nhận tem LOT:…, HISENSE:mã:lô, GS1
 * (01…10…) hoặc số lô gõ tay. Trả null khi không phải mã lô — màn quét báo "không đọc được mã", không đoán.
 */
export function lotScan(raw: string, parse: (text: string) => ParsedBarcode): { lotNumber: string; productCode: string | null } | null {
  const text = raw.trim();
  try {
    const parsed = parse(text);
    return { lotNumber: parsed.lotNumber, productCode: parsed.kind === "productLot" ? parsed.productCode : null };
  } catch {
    return /^[A-Za-z0-9][A-Za-z0-9._/-]{0,39}$/.test(text) ? { lotNumber: text, productCode: null } : null;
  }
}

/** Chỉ số lô của một lần quét (xem lotScan). */
export const lotFromScan = (raw: string, parse: (text: string) => ParsedBarcode): string | null => lotScan(raw, parse)?.lotNumber ?? null;

/** Tem có mã hàng thì chỉ nhận chỗ hàng đúng mã đó (hai mã hàng có thể trùng số lô của nhà cung cấp). */
export const sameProduct = (scannedCode: string | null, productCode: string) => !scannedCode || productCode.trim() === scannedCode;

/**
 * Số lượng gõ tay phải "sạch" theo đúng mẫu máy chủ nhận (quantityToUnits): tối đa 8 chữ số nguyên, 4 chữ số thập phân,
 * dấu chấm thập phân, không dấu phân cách hàng nghìn — không đoán "1,5" là gì. Có thể là 0 (số đếm); dương thì màn tự kiểm.
 */
export const cleanQuantity = (value: string) => /^(?:0|[1-9]\d{0,7})(?:\.\d{1,4})?$/.test(value.trim());

/** Vị trí từ nội dung quét: nhận "LOC:<mã>" (tem vị trí) hoặc gõ tay. */
export const locationFromScan = (raw: string) => raw.trim().replace(/^LOC:/i, "").trim();

/** Lỗi của một lần quét: mã không đọc được thì nói rõ mã đó; lỗi khác dùng câu chung của API. */
export function scanError(e: unknown, t: TFunction, code: string): string {
  if (e instanceof ApiClientError && e.code === "device_parse") return t("scan.parseError", { code });
  return getLocalizedError(e, t, "scan.failed");
}
